const triggerDownload = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const objectUrl = URL.createObjectURL(blob);
  triggerDownload(objectUrl, fileName);
  setTimeout(() => {
    URL.revokeObjectURL(objectUrl);
  }, 1000);
};

const normalizeOrigin = (value?: string | null) => {
  if (!value) {
    return null;
  }

  try {
    return new URL(value).origin;
  } catch {
    return null;
  }
};

const shouldSendAuthToDownloadUrl = (url: string) => {
  const browserOrigin = typeof window !== 'undefined' ? normalizeOrigin(window.location.origin) : null;
  const apiOrigin = normalizeOrigin(process.env.REACT_APP_API_URL);
  const trustedOrigins = new Set([browserOrigin, apiOrigin].filter((origin): origin is string => Boolean(origin)));

  try {
    const resolvedUrl = new URL(url, browserOrigin || apiOrigin || 'http://localhost');
    return trustedOrigins.has(resolvedUrl.origin);
  } catch {
    return false;
  }
};

export const getFilenameFromContentDisposition = (contentDisposition?: string | null) => {
  if (!contentDisposition) {
    return '';
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    const rawName = utf8Match[1].replace(/^"|"$/g, '').trim();

    try {
      return decodeURIComponent(rawName);
    } catch {
      return rawName;
    }
  }

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1]?.trim() || '';
};

export const buildApiDownloadUrl = (path: string) => {
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  return apiBaseUrl ? new URL(path, apiBaseUrl).toString() : path;
};

type DownloadFileFromApiOptions = {
  url: string;
  fallbackFileName: string;
  accessToken?: string | null;
};

export const downloadFileFromApi = async ({ url, fallbackFileName, accessToken }: DownloadFileFromApiOptions) => {
  const headers: Record<string, string> = {};
  const trimmedAccessToken = accessToken?.trim();
  const shouldSendCredentials = shouldSendAuthToDownloadUrl(url);

  if (trimmedAccessToken && shouldSendCredentials) {
    headers.Authorization = `Bearer ${trimmedAccessToken}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    credentials: shouldSendCredentials ? 'include' : 'omit',
    ...(Object.keys(headers).length ? { headers } : {})
  });

  if (!response.ok) {
    const error = new Error('Failed to download file') as Error & { status?: number };
    error.status = response.status;
    throw error;
  }

  const buffer = await response.arrayBuffer();
  const contentType = response.headers.get('Content-Type') || 'application/zip';
  const blob = new Blob([buffer], { type: contentType });
  const fileName = getFilenameFromContentDisposition(response.headers.get('Content-Disposition')) || fallbackFileName;

  triggerBlobDownload(blob, fileName);
};
